import { apiFetch, getApiBaseUrl, getAuthToken } from "@/lib/api";

export type ProspectBusinessType = "operator" | "agency" | "final_client";

export type FinalClientType =
  | "aviation"
  | "beauty"
  | "corporate"
  | "direct_consumer"
  | "esg"
  | "event"
  | "fashion"
  | "food_and_beverage"
  | "government"
  | "health"
  | "marine"
  | "mice"
  | "sports"
  | "trade_show";

export type ProspectContact = {
  name?: string | null;
  position?: string | null;
  email?: string | null;
  telephone?: string | null;
  is_primary?: boolean | null;
  no_response?: boolean | null;
};

export type ProspectNote = {
  id?: string | null;
  author_id?: string | null;
  type?: string | null;
  text: string;
  created_at?: string | null;
};

export type ProspectStageHistory = {
  stage: string;
  entered_at?: string | null;
};

export type Prospect = {
  id: number;
  company_name: string;
  business_type: ProspectBusinessType;
  final_client_types?: FinalClientType[] | null;
  country?: string | null;
  address?: string | null;
  website?: string | null;
  referred_from?: string | null;
  stage?: string | null;
  partner_id?: number | null;
  owner_id?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  created_by?: string | null;
  last_contact_at?: string | null;
  next_follow_up_at?: string | null;
  contacts?: ProspectContact[] | null;
  notes?: ProspectNote[] | null;
  stage_history?: ProspectStageHistory[] | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ProspectListItem = {
  id: number;
  company_name: string;
  business_type?: ProspectBusinessType | null;
  final_client_types?: FinalClientType[] | null;
  country?: string | null;
  website?: string | null;
  stage?: string | null;
  partner_id?: number | null;
  owner_id?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  last_contact_at?: string | null;
  next_follow_up_at?: string | null;
  updated_at?: string | null;
};

export type ProspectListResponse = {
  items: ProspectListItem[];
  total: number;
};

export type ProspectImportResult = {
  created: number;
  updated: number;
  contacts_added: number;
  duplicates_skipped: number;
  errors: number;
};

export type ProspectBulkOwnerUpdateResponse = {
  updated: number;
};

export type ProspectQualifyPartnerResponse = {
  prospect: Prospect;
  partner: {
    id: number;
    name: string;
    country?: string | null;
    address?: string | null;
  } | null;
  client: {
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    company?: string | null;
    client_types?: string[] | null;
  } | null;
  created: boolean;
  already_exists: boolean;
};

export const fetchProspects = async (params: {
  page: number;
  size: number;
  search?: string;
  sortField?: string | null;
  sortOrder?: string | null;
}): Promise<ProspectListResponse> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.search) query.set("search", params.search);
  if (params.sortField) query.set("sortField", params.sortField);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  const payload = await apiFetch<ProspectListResponse | null>(`/prospects?${query.toString()}`);
  return payload ?? { items: [], total: 0 };
};

export const getProspect = async (id: number): Promise<Prospect> => {
  return apiFetch<Prospect>(`/prospects/${id}`);
};

export const createProspect = async (data: Partial<Prospect>): Promise<Prospect> => {
  return apiFetch<Prospect>("/prospects", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateProspect = async (id: number, data: Partial<Prospect>): Promise<Prospect> => {
  return apiFetch<Prospect>(`/prospects/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const updateProspectOwner = async (id: number, ownerId: string | null): Promise<Prospect> => {
  return apiFetch<Prospect>(`/prospects/${id}/owner`, {
    method: "PUT",
    body: JSON.stringify({ owner_id: ownerId }),
  });
};

export const bulkUpdateProspectOwner = async (
  ids: number[],
  ownerId: string | null
): Promise<ProspectBulkOwnerUpdateResponse> => {
  return apiFetch<ProspectBulkOwnerUpdateResponse>("/prospects/owner/bulk", {
    method: "PUT",
    body: JSON.stringify({ ids, owner_id: ownerId }),
  });
};

export const deleteProspect = async (id: number): Promise<void> => {
  await apiFetch<void>(`/prospects/${id}`, {
    method: "DELETE",
  });
};

export const deleteAllProspects = async (): Promise<number> => {
  const batchSize = 200;
  const deleteChunkSize = 20;
  let deleted = 0;

  // Always load page 1 because records are removed while iterating.
  while (true) {
    const response = await fetchProspects({
      page: 1,
      size: batchSize,
      sortField: "created_at",
      sortOrder: "desc",
    });
    const ids = (response.items || [])
      .map((item) => item.id)
      .filter((id): id is number => typeof id === "number");

    if (ids.length === 0) break;

    for (let index = 0; index < ids.length; index += deleteChunkSize) {
      const chunk = ids.slice(index, index + deleteChunkSize);
      await Promise.all(chunk.map((id) => deleteProspect(id)));
    }

    deleted += ids.length;
    if (ids.length < batchSize) break;
  }

  return deleted;
};


export const addProspectNote = async (
  id: number,
  data: { text: string; type?: string | null; contacted_at?: string | null; next_follow_up_at?: string | null }
): Promise<Prospect> => {
  return apiFetch<Prospect>(`/prospects/${id}/notes`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const deleteProspectNote = async (
  id: number,
  data: { id?: string | null; created_at?: string | null; text?: string | null; type?: string | null }
): Promise<Prospect> => {
  return apiFetch<Prospect>(`/prospects/${id}/notes/delete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const importProspects = async (file: File): Promise<ProspectImportResult> => {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch<ProspectImportResult>("/prospects/import", {
    method: "POST",
    body: formData,
  });
};

export const downloadProspectsImportTemplate = async (): Promise<Blob> => {
  const token = getAuthToken();
  const response = await fetch(`${getApiBaseUrl()}/prospects/import/template`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!response.ok) {
    let message = response.statusText || "Failed to download template";
    try {
      const payload = await response.json();
      message = payload?.message || payload?.error || message;
    } catch {
      // Ignore JSON parsing errors and keep default message.
    }
    throw new Error(message);
  }

  return response.blob();
};

export const qualifyProspectToPartner = async (id: number): Promise<ProspectQualifyPartnerResponse> => {
  return apiFetch<ProspectQualifyPartnerResponse>(`/prospects/${id}/qualify-partner`, {
    method: "POST",
  });
};

export const qualifyProspectToPartnerWithBusinessType = async (
  id: number,
  businessType: ProspectBusinessType,
  finalClientTypes?: FinalClientType[] | null
): Promise<ProspectQualifyPartnerResponse> => {
  return apiFetch<ProspectQualifyPartnerResponse>(`/prospects/${id}/qualify-partner`, {
    method: "POST",
    body: JSON.stringify({ business_type: businessType, final_client_types: finalClientTypes ?? [] }),
  });
};
